'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { pick, isEqual } from 'underscore';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UIToggle from 'UIComponents/input/UIToggle';
import UIList from 'UIComponents/list/UIList';
import UISelect from 'UIComponents/input/UISelect';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import Small from 'UIComponents/elements/Small';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { VISIBILITY } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { getControllingFields, findMatchingFields } from 'DesignData/helpers/customWidgetFieldsHelper';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import ToolExpandableSection from 'DesignComponents/tool/ToolExpandableSection';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import ErrorIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/ErrorIcon';
import WarningIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/WarningIcon';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import { OPERATORS } from 'ContentUtils/constants/DisplayConditions';
import { valueIsObject } from 'ContentUtils/helpers/FieldHelpers';

const sidebarI18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.${key}`;

const i18nKey = key => sidebarI18nKey(`displayConditions.${key}`);

const IS_EMPTY_REGEX = '^w*$';

function makeMapStateToProps() {
  return (state, props) => {
    const getCustomWidgetBufferFields = customWidgetBuffersSelectors.makeGetCustomWidgetBufferFields();
    const fields = getCustomWidgetBufferFields(state, props);
    return {
      controllingFields: getControllingFields(fields, props.field.name)
    };
  };
}

class DisplayConditions extends PureComponent {
  constructor(props) {
    super(props);

    this.onToggleClick = e => {
      const checked = e.target.checked;
      this.setState({
        enabled: checked
      });
      this.props.editField({
        [VISIBILITY]: checked ? this.state.visibility : null
      });
    };

    this.onVisibilityPropertyChange = (visibilityProperty, e) => {
      const value = e.target.value;
      const visibility = this.props.field[VISIBILITY] || {};
      this.setState({
        enabled: true,
        visibility: Object.assign({}, this.state.visibility, {
          [visibilityProperty]: value
        })
      });
      this.props.editField({
        [VISIBILITY]: Object.assign({}, visibility, {
          [visibilityProperty]: value
        })
      });
    };

    this.filterControllingFieldsOptions = (options, input) => {
      if (!input.length) {
        return options;
      }

      return findMatchingFields(options, input, false);
    };

    this.state = this.getState(props.field);
  }

  componentDidMount() {
    const visibility = this.props.field[VISIBILITY];

    if (visibility && !visibility.operator && this.state.visibility.operator) {
      this.props.editField({
        [VISIBILITY]: this.state.visibility
      });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      field
    } = prevProps;
    const {
      field: nextField
    } = this.props;

    if (field.id !== nextField.id || !isEqual(field.visibility, nextField.visibility)) {
      this.setState(this.getState(nextField));
    }
  }

  getState(field) {
    const visibility = field[VISIBILITY];
    const {
      controlling_value_regex: controllingValueRegex
    } = visibility || {};
    let operator = visibility ? visibility.operator : null;

    if (!operator) {
      if (controllingValueRegex === IS_EMPTY_REGEX) {
        operator = OPERATORS.EMPTY;
      } else {
        operator = controllingValueRegex ? OPERATORS.MATCHES_REGEX : OPERATORS.NOT_EMPTY;
      }
    }

    return {
      enabled: !!visibility,
      visibility: Object.assign({}, visibility, {
        operator
      })
    };
  }

  getControllingFieldsOptions() {
    return this.props.controllingFields.map(_field => {
      return Object.assign({
        text: _field.label,
        value: _field.name,
        help: _field.name
      }, _field);
    });
  }

  getOperatorsOptions() {
    return Object.keys(OPERATORS).map(_operator => {
      return {
        text: text(i18nKey(`operators.${_operator}`)),
        value: _operator
      };
    });
  }

  renderFieldValue({
    help
  }) {
    if (!help) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('placeholders.field')
      });
    }

    return /*#__PURE__*/_jsx("span", {
      className: "controlling-field-name is--code",
      children: /*#__PURE__*/_jsx(UITruncateString, {
        children: help
      })
    });
  }

  renderDisplayConditionsHelpTooltip() {
    const title = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('helpTooltip.explanation')
          })
        }), /*#__PURE__*/_jsxs(UIList, {
          styled: true,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.examples.address')
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.examples.imageDescription')
          })]
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('helpTooltip.moreHelp')
        }), /*#__PURE__*/_jsx(UILink, {
          className: "m-left-1",
          target: "_blank",
          href: "http://designers.hubspot.com/docs/modules/field-options-and-usage#display-conditions",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.learnMore')
          })
        })]
      })
    });

    return /*#__PURE__*/_jsx(UIHelpIcon, {
      title: title,
      color: BATTLESHIP,
      tooltipPlacement: "top left",
      tooltipProps: {
        headingText: text(i18nKey('helpTooltip.headingText')),
        className: 'field-edit-help-tooltip',
        use: 'longform'
      }
    });
  }

  renderDisplayConditionsTitle() {
    const displayConditionsErrors = pick(this.props.fieldErrors, ['controlling_field', 'controlling_value_regex']);
    const displayConditionsWarnings = pick(this.props.fieldWarnings, ['controlling_field', 'controlling_value_regex']);
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: displayConditionsErrors,
        i18nKey: sidebarI18nKey('contentOptions.hasFieldError')
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: "m-right-2",
        fieldWarnings: displayConditionsWarnings,
        i18nKey: sidebarI18nKey('contentOptions.hasFieldWarning')
      }), /*#__PURE__*/_jsx("span", {
        className: "m-right-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('accordionLabel')
        })
      }), this.renderDisplayConditionsHelpTooltip()]
    });
  }

  render() {
    const {
      enabled,
      visibility
    } = this.state;
    const {
      controllingFields,
      field,
      fieldErrors,
      fieldWarnings,
      onOpenChange,
      open,
      readOnly
    } = this.props;
    const {
      //legacy property, using controlling_field_path now
      controlling_field: controllingFieldId,
      controlling_field_path: controllingFieldPath,
      controlling_value_regex: controllingValueRegex,
      operator,
      property
    } = visibility;
    const controllingField = controllingFields.find(_field => {
      return controllingFieldPath ? _field.name === controllingFieldPath : _field.id === controllingFieldId;
    });

    const toggle = /*#__PURE__*/_jsx(UIToggle, {
      checked: enabled,
      onChange: this.onToggleClick,
      size: "extra-small",
      readOnly: readOnly
    });

    const disableValueField = operator === OPERATORS.EMPTY || operator === OPERATORS.NOT_EMPTY || readOnly;
    const valuePlaceholder = operator === OPERATORS.MATCHES_REGEX ? text(i18nKey('placeholders.regex')) : text(i18nKey('placeholders.value'));
    return /*#__PURE__*/_jsx(ToolExpandableSection, {
      actions: toggle,
      containerClassName: "field-edit-accordion-container",
      titleText: this.renderDisplayConditionsTitle(),
      onOpenChange: onOpenChange,
      open: open,
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsxs(ToolFieldset, {
          children: [/*#__PURE__*/_jsx(FormControlWithErrors, {
            className: "m-bottom-2",
            errors: fieldErrors.controlling_field,
            label: text(i18nKey('conditionLabel')),
            children: /*#__PURE__*/_jsx(SidebarInput, {
              dropdownClassName: "controlling-field-select-dropdown",
              inputComponent: UISelect,
              onChange: (...partialArgs) => this.onVisibilityPropertyChange('controlling_field_path', ...partialArgs),
              filterOptions: this.filterControllingFieldsOptions,
              options: this.getControllingFieldsOptions(),
              placeholder: text(i18nKey('placeholders.field')),
              value: controllingField && controllingField.name,
              valueRenderer: this.renderFieldValue,
              disabled: readOnly
            })
          }), valueIsObject(controllingField) && /*#__PURE__*/_jsxs(UIFlex, {
            align: "center",
            children: [/*#__PURE__*/_jsx(SidebarInput, {
              className: "m-bottom-2",
              onChange: (...partialArgs) => this.onVisibilityPropertyChange('property', ...partialArgs),
              placeholder: text(i18nKey('placeholders.property')),
              value: property || '',
              disabled: readOnly
            }), /*#__PURE__*/_jsx(UIHelpIcon, {
              className: "m-left-1",
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('helpTooltip.property')
              }),
              color: BATTLESHIP,
              tooltipPlacement: "top left"
            })]
          }), /*#__PURE__*/_jsx(SidebarInput, {
            className: "m-bottom-2",
            inputComponent: UISelect,
            onChange: (...partialArgs) => this.onVisibilityPropertyChange('operator', ...partialArgs),
            options: this.getOperatorsOptions(),
            placeholder: text(i18nKey('placeholders.operator')),
            value: operator,
            disabled: readOnly
          }), /*#__PURE__*/_jsx(FormControlWithErrors, {
            errors: fieldErrors.controlling_value_regex || fieldWarnings.controlling_value_regex,
            children: /*#__PURE__*/_jsx(SidebarInput, {
              disabled: disableValueField,
              onChange: (...partialArgs) => this.onVisibilityPropertyChange('controlling_value_regex', ...partialArgs),
              placeholder: valuePlaceholder,
              value: disableValueField ? '' : controllingValueRegex
            })
          })]
        })
      })
    }, `${field.name}-repeater-options`);
  }

}

DisplayConditions.propTypes = {
  controllingFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  fieldWarnings: PropTypes.object,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  readOnly: PropTypes.bool
};
DisplayConditions.defaultProps = {
  fieldErrors: {},
  fieldWarnings: {}
};
export default connect(makeMapStateToProps)(DisplayConditions);